/** Public OAuth client ids exposed to the browser via Vite env vars. */
import { baseUrl } from '../constant/constant';

export const googleClientId = (import.meta.env.VITE_GOOGLE_CLIENT_ID || '').trim();

export const isGoogleLoginConfigured = () => googleClientId.length > 0;

/**
 * Origin of the API / apex site (https://apsession.com). This is the ONE
 * origin registered as an authorized JavaScript origin on the Google OAuth
 * client, and the host that serves the /oauth/* redirect routes.
 */
function apiOrigin() {
    try {
        return new URL(baseUrl, typeof window !== 'undefined' ? window.location.href : undefined).origin;
    } catch {
        return '';
    }
}

function stripWww(host) {
    return host.toLowerCase().replace(/^www\./, '');
}

/**
 * Should "Continue with Google" leave the page instead of opening the popup?
 *
 * The Google Identity Services popup only works on origins listed one by one
 * in the Google Cloud Console. apsession.com (and its www alias) are listed;
 * event short hosts like foammadnessja.apsession.com are minted per event and
 * cannot be, so Google refuses them with `origin_mismatch`. On any origin
 * other than the API's own we go through the redirect flow instead, which
 * checks only the single fixed callback URL on the apex.
 *
 * Local dev keeps the popup: there `baseUrl` resolves to the page's own
 * origin (see constant.js), so the two match.
 */
export function shouldUseGoogleRedirect() {
    if (typeof window === 'undefined') return false;
    const api = apiOrigin();
    if (!api) return false;
    try {
        const here = new URL(window.location.href);
        const there = new URL(api);
        if (here.protocol !== there.protocol) return true;
        return stripWww(here.host) !== stripWww(there.host);
    } catch {
        return false;
    }
}

/**
 * Where the redirect flow starts. The backend parks `return` (this page), bounces
 * through accounts.google.com, and sends the browser back here with the outcome
 * in the URL fragment — see GoogleRedirectReturn.jsx for the landing side.
 */
export function googleRedirectStartUrl(returnUrl) {
    const api = apiOrigin();
    return `${api}/oauth/google/start?return=${encodeURIComponent(returnUrl)}`;
}

/** Fragment keys the backend uses to report the outcome of the redirect flow. */
export const GOOGLE_REDIRECT_HANDOFF_KEY = 'gauth';
export const GOOGLE_REDIRECT_ERROR_KEY = 'gauth_error';

export const GOOGLE_REDIRECT_ERROR_MESSAGES = {
    cancelled: '', // visitor closed Google's account chooser — nothing to say
    expired: 'Your Google sign-in took too long. Please try again.',
    denied: 'Google did not complete the sign-in. Please try again.',
    failed: 'Google sign-in failed. Please try again.',
    not_configured: 'Google sign-in is temporarily unavailable. Please use email and password.',
};
